import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, type Me, api } from '../api.js';
import { relativeTime, statusLabel, typeLabel } from '../format.js';

const NEXT_STATUSES: Record<string, string[]> = {
  new: ['triaged', 'needs_info', 'duplicate', 'wont_fix', 'not_reproducible'],
  triaged: ['accepted', 'needs_info', 'duplicate', 'wont_fix', 'not_reproducible'],
  accepted: ['in_progress', 'needs_info', 'wont_fix'],
  in_progress: ['in_review', 'needs_info'],
  in_review: ['released', 'in_progress'],
  released: ['verified', 'in_progress'],
  verified: ['closed'],
  needs_info: ['triaged', 'closed', 'not_reproducible'],
  closed: ['triaged'],
  duplicate: ['triaged'],
  wont_fix: ['triaged'],
  not_reproducible: ['triaged'],
};

const TERMINAL = ['closed', 'duplicate', 'wont_fix', 'not_reproducible'];

interface Props {
  me: Me | null;
}

export function Detail({ me }: Props) {
  const { ref } = useParams<{ ref: string }>();
  const [item, setItem] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [comment, setComment] = useState('');

  const load = useCallback(async () => {
    if (!ref) return;
    try {
      const detail = await api.detail(ref);
      setItem(detail);
      setError(null);
      const reporterHistory = await api.reporterHistory(detail.id as string).catch(() => []);
      setHistory(reporterHistory);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'could not load this report');
    }
  }, [ref]);

  useEffect(() => {
    void load();
  }, [load]);

  const transition = async (status: string) => {
    if (!item) return;

    // The server refuses a terminal move with no explanation, so the reason is
    // collected here rather than letting the person hit a wall.
    if (TERMINAL.includes(status) && !note.trim()) {
      setError(`Add a note explaining why before moving this to ${statusLabel(status)}.`);
      return;
    }

    setBusy(true);
    try {
      await api.update(item.id, { status, note: note.trim() || undefined });
      setNote('');
      setError(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'could not update this report');
    } finally {
      setBusy(false);
    }
  };

  const addComment = async (visibility: 'internal' | 'reporter_visible') => {
    if (!item || !comment.trim()) return;
    setBusy(true);
    try {
      await api.comment(item.id, comment.trim(), visibility);
      setComment('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'could not add the comment');
    } finally {
      setBusy(false);
    }
  };

  if (error && !item) return <div className="notice error">{error}</div>;
  if (!item) return <div className="empty">Loading…</div>;

  const diagnostics = item.diagnostics;
  const context = item.context;
  const integrity = item.auditIntegrity;

  return (
    <>
      <p>
        <Link to="/">← Back to the inbox</Link>
      </p>

      <h2 style={{ margin: '0 0 4px' }}>{item.title}</h2>
      <p className="muted" style={{ marginBlockStart: 0 }}>
        <span className="ref">{item.publicRef}</span> · {item.application.displayName}
        {item.component && ` · ${item.component.name}`} · {typeLabel(item.type)} ·{' '}
        <span className={`badge sev-${item.severity}`}>{item.severity}</span> ·{' '}
        {statusLabel(item.status)}
      </p>

      {error && <div className="notice error">{error}</div>}

      <div className="detail">
        <div>
          <div className="card">
            <h3>What happened</h3>
            <div className="prose">{item.description}</div>

            {item.expectedBehavior && (
              <>
                <h3 style={{ marginBlockStart: 14 }}>What was expected</h3>
                <div className="prose">{item.expectedBehavior}</div>
              </>
            )}
            {item.steps && (
              <>
                <h3 style={{ marginBlockStart: 14 }}>Steps to reproduce</h3>
                <div className="prose">{item.steps}</div>
              </>
            )}
          </div>

          {item.attachments?.length > 0 && (
            <div className="card">
              <h3>Attachments</h3>
              {item.attachments.map((attachment: any) => (
                <div key={attachment.id} style={{ marginBlockEnd: 12 }}>
                  {attachment.url && attachment.contentType?.startsWith('image/') ? (
                    <a href={attachment.url} target="_blank" rel="noreferrer">
                      <img
                        src={attachment.url}
                        alt={attachment.fileName}
                        style={{ maxWidth: '100%', border: '1px solid var(--border)' }}
                      />
                    </a>
                  ) : (
                    <a href={attachment.url ?? '#'} target="_blank" rel="noreferrer">
                      {attachment.fileName}
                    </a>
                  )}
                  <div className="muted" style={{ fontSize: 12 }}>
                    {Math.round(attachment.byteSize / 1024)} KB
                    {attachment.redactionApplied && ' · areas were hidden before upload'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {diagnostics && (
            <div className="card">
              <h3>Technical detail</h3>
              {diagnostics.primaryErrorSignature && (
                <>
                  <div className="muted" style={{ fontSize: 12, marginBlockEnd: 4 }}>
                    Most relevant error
                  </div>
                  <pre>{diagnostics.primaryErrorSignature}</pre>
                </>
              )}
              {diagnostics.jsErrors?.length > 0 && (
                <>
                  <div className="muted" style={{ fontSize: 12, margin: '12px 0 4px' }}>
                    Errors ({diagnostics.jsErrors.length})
                  </div>
                  <pre>
                    {diagnostics.jsErrors
                      .map((e: any) => `${e.message}\n${e.stack ?? ''}`)
                      .join('\n\n')}
                  </pre>
                </>
              )}
              {diagnostics.networkEntries?.length > 0 && (
                <>
                  <div className="muted" style={{ fontSize: 12, margin: '12px 0 4px' }}>
                    Failed requests
                  </div>
                  <pre>
                    {diagnostics.networkEntries
                      .map((n: any) => `${n.status ?? 'failed'} ${n.method} ${n.url}`)
                      .join('\n')}
                  </pre>
                </>
              )}
              {diagnostics.scrubbed && (
                <p className="muted" style={{ fontSize: 12 }}>
                  Sensitive values were redacted from this diagnostic before it was stored.
                </p>
              )}
            </div>
          )}

          <div className="card">
            <h3>Discussion</h3>
            {item.comments?.length === 0 && <p className="muted">No comments yet.</p>}
            {item.comments?.map((entry: any) => (
              <div key={entry.id} className={`comment ${entry.visibility}`}>
                <div className="comment-meta">
                  {entry.author.displayName ?? entry.author.email} · {relativeTime(entry.createdAt)}
                </div>
                <div className="prose">{entry.body}</div>
              </div>
            ))}

            {me?.capabilities.canTriage && (
              <div style={{ marginBlockStart: 12 }}>
                <textarea
                  placeholder="Add a comment…"
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                />
                <div style={{ display: 'flex', gap: 8, marginBlockStart: 8 }}>
                  <button
                    type="button"
                    className="primary"
                    disabled={busy || !comment.trim()}
                    onClick={() => void addComment('internal')}
                  >
                    Add internal note
                  </button>
                  <button
                    type="button"
                    className="chip"
                    disabled={busy || !comment.trim()}
                    onClick={() => void addComment('reporter_visible')}
                  >
                    Reply to the reporter
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <aside>
          {me?.capabilities.canTriage && (
            <div className="card">
              <h3>Move this along</h3>
              <textarea
                placeholder="Note (required when closing)"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                style={{ minHeight: 60 }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBlockStart: 8 }}>
                {(NEXT_STATUSES[item.status] ?? []).map((status) => (
                  <button
                    key={status}
                    type="button"
                    className="chip"
                    disabled={busy}
                    onClick={() => void transition(status)}
                  >
                    {statusLabel(status)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <h3>Reported by</h3>
            <dl className="facts">
              <dt>Name</dt>
              <dd>{item.reporter.displayName ?? '—'}</dd>
              <dt>Email</dt>
              <dd>{item.reporter.email ?? '—'}</dd>
              <dt>Department</dt>
              <dd>{item.reporter.department ?? '—'}</dd>
              <dt>Job title</dt>
              <dd>{item.reporter.jobTitle ?? '—'}</dd>
              <dt>Raised</dt>
              <dd>{new Date(item.createdAt).toLocaleString()}</dd>
            </dl>

            {history.length > 1 && (
              <>
                <div className="muted" style={{ fontSize: 12, margin: '12px 0 4px' }}>
                  Also reported ({history.length - 1})
                </div>
                {history
                  .filter((entry) => entry.id !== item.id)
                  .slice(0, 6)
                  .map((entry) => (
                    <div key={entry.id} style={{ fontSize: 12 }}>
                      <Link to={`/item/${entry.publicRef}`}>{entry.publicRef}</Link> {entry.title}
                    </div>
                  ))}
              </>
            )}
          </div>

          {context && (
            <div className="card">
              <h3>Where it happened</h3>
              <dl className="facts">
                <dt>Page</dt>
                <dd>{context.route}</dd>
                <dt>Environment</dt>
                <dd>{item.env}</dd>
                <dt>Build</dt>
                <dd>{context.buildCommit ?? context.appVersion ?? '—'}</dd>
                <dt>Browser</dt>
                <dd>
                  {context.browserName ?? '—'} {context.browserVersion ?? ''}
                </dd>
                <dt>System</dt>
                <dd>{context.osName ?? '—'}</dd>
                <dt>Screen</dt>
                <dd>
                  {context.viewportWidth
                    ? `${context.viewportWidth}×${context.viewportHeight}`
                    : '—'}
                </dd>
                <dt>Language</dt>
                <dd>
                  {item.locale} ({context.dir ?? 'ltr'})
                </dd>
              </dl>
            </div>
          )}

          <div className="card">
            <h3>History</h3>
            {item.statusHistory?.map((entry: any) => (
              <div key={entry.id} style={{ fontSize: 12, marginBlockEnd: 6 }}>
                <strong>{statusLabel(entry.toStatus)}</strong>
                {entry.fromStatus && (
                  <span className="muted"> from {statusLabel(entry.fromStatus)}</span>
                )}
                <div className="muted">
                  {entry.actor?.displayName ?? entry.actorSource} · {relativeTime(entry.occurredAt)}
                </div>
                {entry.note && <div className="prose">{entry.note}</div>}
              </div>
            ))}

            {integrity && (
              <div
                className={`notice ${integrity.valid ? 'ok' : 'error'}`}
                style={{ marginBlockStart: 10, fontSize: 12 }}
              >
                {integrity.valid
                  ? `Audit trail verified (${integrity.length} entries).`
                  : `Audit trail failed verification: ${integrity.reason}`}
              </div>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}
